import { usePurchaseOrder } from '@/hooks/usePurchaseOrders';
import { PurchaseOrderDetails } from '@/components/purchase-orders/PurchaseOrderDetails';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useToast } from '@/hooks/use-toast';
import { useState, useEffect, useMemo } from 'react';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Printer, AlertCircle, Edit, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FinancialAdjustmentForm } from '@/components/purchase-orders/FinancialAdjustmentForm';
import { AdjustmentHistory } from '@/components/purchase-orders/AdjustmentHistory';
import { AdjustmentPaymentsList } from '@/components/purchase-orders/AdjustmentPaymentsList';
import { FinancialSummaryCard } from '@/components/purchase-orders/FinancialSummaryCard';
import type { POModificationRequest } from '@/lib/types/job';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CancellationRequestDialog } from '@/components/purchase-orders/CancellationRequestDialog';
import { VendorCancellationResponseDialog } from '@/components/purchase-orders/VendorCancellationResponseDialog';
import { ClientCancellationFeeReviewCard } from '@/components/purchase-orders/ClientCancellationFeeReviewCard';
import { CancellationHistory } from '@/components/purchase-orders/CancellationHistory';
import { CancellationStatusBadge } from '@/components/purchase-orders/CancellationStatusBadge';

export default function PurchaseOrderDetailsPage() {
  const { poId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const [accepting, setAccepting] = useState(false);
  const [showAdjustmentDialog, setShowAdjustmentDialog] = useState(false);
  const [showAdjustmentHistory, setShowAdjustmentHistory] = useState(false);
  const [showCancellationDialog, setShowCancellationDialog] = useState(false);
  const [isVendorResponseDialogOpen, setIsVendorResponseDialogOpen] = useState(false);
  const [vendorResponseSubmitted, setVendorResponseSubmitted] = useState(false);
  const [pendingAdjustments, setPendingAdjustments] = useState<POModificationRequest[]>([]);
  
  const { purchaseOrder, loading, error, refetch } = usePurchaseOrder(poId);
  const queryClient = useQueryClient();

  // Fetch adjustment payment transactions
  const { data: adjustmentTransactions } = useQuery({
    queryKey: ['adjustment-transactions', purchaseOrder?.id],
    queryFn: async () => {
      if (!purchaseOrder?.id) return [];
      
      const { data } = await supabase
        .from('payment_transactions')
        .select('*')
        .eq('purchase_order_id', purchaseOrder.id)
        .eq('payment_context', 'adjustment_payment')
        .order('created_at', { ascending: false });
      
      return data || [];
    },
    enabled: !!purchaseOrder?.id,
    refetchOnWindowFocus: true,
  });

  // Fetch all adjustments for financial summary
  const { data: allAdjustments = [] } = useQuery({
    queryKey: ['all-adjustments', purchaseOrder?.id],
    queryFn: async () => {
      if (!purchaseOrder?.id) return [];
      
      const { data } = await supabase
        .from('po_modification_requests')
        .select('*')
        .eq('purchase_order_id', purchaseOrder.id)
        .order('created_at', { ascending: false });
      
      return (data || []) as POModificationRequest[];
    },
    enabled: !!purchaseOrder?.id,
    refetchOnWindowFocus: true,
  });

  // Note: Initial payment data is now fetched by FinancialSummaryCard via useInitialPaymentLedger
  // This provides consistent data across ALL payment types using the Financial Ledger

  // Fetch pending adjustments
  useEffect(() => {
    if (!purchaseOrder) return;

    const fetchPendingAdjustments = async () => {
      const { data } = await supabase
        .from('po_modification_requests')
        .select('*, purchase_orders(po_amount_cents, po_number)')
        .eq('purchase_order_id', purchaseOrder.id)
        .eq('status', 'pending');
      
      setPendingAdjustments((data || []) as POModificationRequest[]);
    };

    fetchPendingAdjustments();

    // Subscribe to changes
    const channel = supabase
      .channel(`po_adjustments_${purchaseOrder.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'po_modification_requests',
          filter: `purchase_order_id=eq.${purchaseOrder.id}`,
        },
        () => {
          fetchPendingAdjustments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [purchaseOrder]);

  // Filter to only show adjustments that require THIS user's approval
  const pendingAdjustmentsRequiringApproval = useMemo(() => {
    if (!user || !purchaseOrder) return [];
    
    return pendingAdjustments.filter(adjustment => {
      // User can approve if they're from the non-initiating party
      if (adjustment.initiating_party === 'client') {
        return user.organization_id === purchaseOrder.vendor_organization_id;
      } else {
        return user.organization_id === purchaseOrder.organization_id;
      }
    });
  }, [pendingAdjustments, user, purchaseOrder]);

  // Check if user can request adjustments (vendors only)
  const canRequestAdjustment = useMemo(() => {
    if (!user || !purchaseOrder) return false;
    
    const isVendorOrg = user.organization_id === purchaseOrder.vendor_organization_id;
    const poIsActive = ['acknowledged', 'current'].includes(purchaseOrder.status);
    
    return isVendorOrg && poIsActive;
  }, [user, purchaseOrder]);

  // Check if user is from the client organization (for showing info note)
  const isClientOrg = useMemo(() => {
    if (!user || !purchaseOrder) return false;
    return user.organization_id === purchaseOrder.organization_id;
  }, [user, purchaseOrder]);

  const userRole = useMemo(() => {
    if (!user || !purchaseOrder) return 'client';
    return user.organization_id === purchaseOrder.vendor_organization_id ? 'vendor' : 'client';
  }, [user, purchaseOrder]);

  // Get pending cancellation request
  const pendingCancellation = useMemo(() => {
    return pendingAdjustments.find(
      adj => adj.request_type === 'cancellation' && 
      ['pending_vendor_response', 'pending_client_approval'].includes(adj.cancellation_status || '')
    );
  }, [pendingAdjustments]);

  // Check if user can see/interact with cancellation button (client only)
  const canShowCancellationButton = useMemo(() => {
    if (!user || !purchaseOrder) return false;
    
    const isClientOrg = user.organization_id === purchaseOrder.organization_id;
    const canCancel = ['acknowledged', 'sent_to_vendor', 'approved'].includes(purchaseOrder.status);
    const jobNotFinalized = !['accepted', 'rejected', 'closed'].includes(purchaseOrder.job?.status);
    
    return isClientOrg && canCancel && jobNotFinalized;
  }, [user, purchaseOrder]);

  // Auto-open vendor response dialog when there's a pending request
  useEffect(() => {
    if (userRole === 'vendor' && 
        pendingCancellation?.cancellation_status === 'pending_vendor_response' &&
        !isVendorResponseDialogOpen &&
        !vendorResponseSubmitted) {
      setIsVendorResponseDialogOpen(true);
    }
  }, [pendingCancellation, userRole, isVendorResponseDialogOpen, vendorResponseSubmitted]);

  // Reset the submitted flag when pendingCancellation changes
  useEffect(() => {
    if (!pendingCancellation || pendingCancellation?.cancellation_status !== 'pending_vendor_response') {
      setVendorResponseSubmitted(false);
    }
  }, [pendingCancellation]);

  const handleVendorAccept = async () => {
    if (!purchaseOrder || !user) return;

    try {
      setAccepting(true);
      
      // Update purchase order to mark as accepted
      const { error: updateError } = await supabase
        .from('purchase_orders')
        .update({
          vendor_acknowledged_at: new Date().toISOString(),
          vendor_acknowledged_by_user_id: user.id,
          status: 'acknowledged',
          updated_at: new Date().toISOString()
        })
        .eq('id', purchaseOrder.id);

      if (updateError) throw updateError;

      // Update job status and workflow_stage to in_progress when vendor accepts PO
      const { error: jobUpdateError } = await supabase
        .from('jobs')
        .update({ 
          status: 'in_progress',
          workflow_stage: 'in_progress'
        })
        .eq('id', purchaseOrder.job_id);

      if (jobUpdateError) {
        console.error('Failed to update job status:', jobUpdateError);
      }

      // Send notification to client that vendor accepted the PO
      try {
        await invokeEdgeFunction('send-po-notification', {
          body: {
            type: 'po_vendor_accepted',
            purchaseOrderId: purchaseOrder.id,
          },
          callerOperation: 'PurchaseOrderDetailsPage.handleVendorAccept',
        });
        console.log('PO acceptance notification sent to client');
      } catch (notifError) {
        console.error('Failed to send PO acceptance notification:', notifError);
        // Non-fatal - don't fail the acceptance
      }

      toast({
        title: 'Purchase Order Accepted',
        description: 'You have successfully accepted this purchase order. Work can now begin.',
      });

      // Refetch to show updated status
      refetch();
    } catch (err) {
      console.error('Error accepting purchase order:', err);
      toast({
        title: 'Error',
        description: 'Failed to accept purchase order. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading purchase order...</div>;
  }

  if (error || !purchaseOrder) {
    return <div className="text-center py-8 text-destructive">Purchase order not found</div>;
  }

  // Only vendors can accept POs
  const isVendor = user?.role === 'vendor';
  
  // Check if job is in a finalized state (read-only)
  const isJobFinalized = purchaseOrder.job && ['accepted', 'rejected', 'closed'].includes(purchaseOrder.job.status);

  const handlePrint = () => {
    window.print();
  };

  // Get default tab from query param or use 'details'
  const defaultTab = searchParams.get('tab') || 'details';

  return (
    <div className="space-y-4">
      {/* Breadcrumbs and Actions - Hidden when printing */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 print:hidden px-4 sm:px-6 pt-4 sm:pt-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/dashboard">Dashboard</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            {purchaseOrder.job && (
              <>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={`/jobs/${purchaseOrder.job.public_id}`}>
                      {purchaseOrder.job.title}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
              </>
            )}
            <BreadcrumbItem>
              <BreadcrumbPage>Purchase Order {purchaseOrder.po_number}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        
        <Button variant="outline" onClick={handlePrint}>
          <Printer className="h-4 w-4 mr-2" />
          Print
        </Button>
      </div>


      {/* Vendor Cancellation Response Dialog */}
      {userRole === 'vendor' && pendingCancellation?.cancellation_status === 'pending_vendor_response' && (
        <VendorCancellationResponseDialog
          open={isVendorResponseDialogOpen}
          onOpenChange={setIsVendorResponseDialogOpen}
          cancellationRequest={pendingCancellation as any}
          onResponseSubmitted={() => setVendorResponseSubmitted(true)}
          jobPublicId={purchaseOrder.job?.public_id}
        />
      )}

      {/* Pending Adjustment Alert */}
      {pendingAdjustmentsRequiringApproval.length > 0 && (
        <Alert className="max-w-7xl mx-auto px-4 sm:px-6 border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/20">
          <AlertCircle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
          <AlertTitle className="text-orange-900 dark:text-orange-100">
            Change Order Pending Your Review
          </AlertTitle>
          <AlertDescription className="text-sm text-orange-800 dark:text-orange-200">
            {pendingAdjustmentsRequiringApproval.length === 1 
              ? 'A change order request requires your approval. Please review it in the Change Orders tab.'
              : `${pendingAdjustmentsRequiringApproval.length} change order requests require your approval. Please review them in the Change Orders tab.`
            }
          </AlertDescription>
        </Alert>
      )}

      {/* Cancellation Status Alert */}
      {pendingCancellation && (
        <Alert className="max-w-7xl mx-auto px-4 sm:px-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle className="flex flex-col sm:flex-row sm:items-center gap-2">
            <span>Purchase Order Cancellation In Progress</span>
            <CancellationStatusBadge status={pendingCancellation.cancellation_status || ''} />
          </AlertTitle>
          <AlertDescription className="text-sm">
            This purchase order is currently being cancelled. See the cancellation timeline below for details.
          </AlertDescription>
        </Alert>
      )}

      {/* Client Cancellation Fee Review */}
      {userRole === 'client' && pendingCancellation?.cancellation_status === 'pending_client_approval' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <ClientCancellationFeeReviewCard 
            cancellationRequest={pendingCancellation as any} 
            jobPublicId={purchaseOrder.job?.public_id}
          />
        </div>
      )}

      {/* Main Content with Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <Tabs defaultValue={defaultTab} className="w-full">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <TabsList className="min-w-[400px] sm:min-w-0">
                <TabsTrigger value="details" className="text-xs sm:text-sm">Purchase Order</TabsTrigger>
                <TabsTrigger value="adjustments" className="text-xs sm:text-sm">Change Orders</TabsTrigger>
              </TabsList>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              {canShowCancellationButton && (
                <Button 
                  variant="destructive" 
                  onClick={() => setShowCancellationDialog(true)} 
                  disabled={!!pendingCancellation}
                  className="w-full sm:w-auto"
                >
                  {pendingCancellation ? 'Cancellation Requested' : 'Request Cancellation'}
                </Button>
              )}
              {!isJobFinalized && (
                <>
                  {canRequestAdjustment && (
                    <Button variant="outline" onClick={() => setShowAdjustmentDialog(true)} className="w-full sm:w-auto">
                      <Edit className="h-4 w-4 mr-2" />
                      Request Change Order
                    </Button>
                  )}
                  {isClientOrg && !canRequestAdjustment && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 px-3 py-2 rounded-md">
                      <Info className="h-4 w-4 flex-shrink-0" />
                      <span>Change orders must be initiated by the vendor</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <TabsContent value="details" className="space-y-3">
            {/* Purchase Order Details */}
            <PurchaseOrderDetails 
              purchaseOrder={purchaseOrder} 
              onVendorAccept={isVendor && !accepting ? handleVendorAccept : undefined}
              isVendor={isVendor}
            />
          </TabsContent>

          <TabsContent value="adjustments" className="space-y-6">
            {/* Financial Summary */}
            <FinancialSummaryCard 
              purchaseOrder={purchaseOrder}
              adjustments={allAdjustments}
            />

            {/* Show cancellation history if exists */}
            {pendingCancellation && (
              <CancellationHistory
                data={{
                  request: pendingCancellation as any,
                  purchaseOrder: {
                    po_number: purchaseOrder.po_number,
                    po_amount_cents: purchaseOrder.po_amount_cents,
                  },
                }}
              />
            )}
            
            <AdjustmentHistory
              purchaseOrderId={purchaseOrder.id}
              purchaseOrder={purchaseOrder}
              onAdjustmentApproved={() => {
                refetch();
                queryClient.invalidateQueries({ queryKey: ['adjustment-transactions', purchaseOrder?.id] });
                queryClient.invalidateQueries({ queryKey: ['all-adjustments', purchaseOrder?.id] });
              }}
              adjustmentTransactions={adjustmentTransactions}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Adjustment Request Dialog */}
      <Dialog open={showAdjustmentDialog} onOpenChange={setShowAdjustmentDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Request Change Order</DialogTitle>
          </DialogHeader>
          <FinancialAdjustmentForm
            purchaseOrder={purchaseOrder}
            userRole={userRole}
            onSubmitted={() => {
              setShowAdjustmentDialog(false);
              refetch();
            }}
            onCancel={() => setShowAdjustmentDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Cancellation Request Dialog */}
      <CancellationRequestDialog
        open={showCancellationDialog}
        onOpenChange={setShowCancellationDialog}
        purchaseOrderId={purchaseOrder.id}
        poNumber={purchaseOrder.po_number}
        onSuccess={() => refetch()}
      />
    </div>
  );
}